import React, { useState, useEffect, useCallback } from 'react';
import { X, Scissors, ArrowLeftRight, Percent, DollarSign, AlertTriangle, Check } from 'lucide-react';

/**
 * SplitInvoiceModal
 * Allows splitting an invoice across departments by line position or by value (percentage/amount).
 * 
 * Props:
 *  - invoice: parent invoice object
 *  - lines: parent invoice line items
 *  - departments: list of { DepartmentID, Name/DepartmentName }
 *  - currencySymbol: string like "£"
 *  - onSplit: async ({ splitType, allocations }) => void
 *  - onClose: () => void
 */
export default function SplitInvoiceModal({ invoice, lines, departments, currencySymbol, onSplit, onClose }) {
  const [splitType, setSplitType] = useState('ByValue'); // 'ByPosition' or 'ByValue'
  const [allocations, setAllocations] = useState([
    { departmentId: '', percentage: 50, amount: 0, lineIds: [] },
    { departmentId: '', percentage: 50, amount: 0, lineIds: [] }
  ]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const totalAmount = parseFloat(invoice?.TotalAmount) || 0;
  const subTotal = parseFloat(invoice?.SubTotal) || 0;

  // Sync amounts when percentage or totalAmount changes
  useEffect(() => {
    if (splitType === 'ByValue') {
      setAllocations(prev => prev.map(a => ({
        ...a,
        amount: parseFloat(((parseFloat(a.percentage) || 0) / 100 * totalAmount).toFixed(2))
      })));
    }
  }, [splitType, totalAmount]);

  const recalcFromPercentage = useCallback((allocs) => {
    return allocs.map(a => ({
      ...a,
      amount: parseFloat(((parseFloat(a.percentage) || 0) / 100 * totalAmount).toFixed(2))
    }));
  }, [totalAmount]);

  const recalcFromAmount = useCallback((allocs) => {
    return allocs.map(a => ({
      ...a,
      percentage: totalAmount > 0 
        ? parseFloat(((parseFloat(a.amount) || 0) / totalAmount * 100).toFixed(2)) 
        : 0
    }));
  }, [totalAmount]);

  const handlePercentageChange = (index, value) => {
    const val = parseFloat(value) || 0;
    setAllocations(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], percentage: val };
      return recalcFromPercentage(updated);
    });
    setError('');
  };

  const handleAmountChange = (index, value) => {
    const val = parseFloat(value) || 0;
    setAllocations(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], amount: val };
      return recalcFromAmount(updated);
    });
    setError('');
  };

  const handleDepartmentChange = (index, deptId) => {
    setAllocations(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], departmentId: deptId };
      return updated;
    });
    setError('');
  };

  const handleLineToggle = (allocIndex, lineId) => {
    setAllocations(prev => {
      const updated = prev.map((a, i) => {
        if (i === allocIndex) {
          const has = a.lineIds.includes(lineId);
          return { ...a, lineIds: has ? a.lineIds.filter(id => id !== lineId) : [...a.lineIds, lineId] };
        }
        // Remove from other allocations
        return { ...a, lineIds: a.lineIds.filter(id => id !== lineId) };
      });
      return updated;
    });
    setError('');
  };

  const addAllocation = () => {
    setAllocations(prev => [...prev, { departmentId: '', percentage: 0, amount: 0, lineIds: [] }]);
  };

  const removeAllocation = (index) => {
    if (allocations.length <= 2) return;
    setAllocations(prev => prev.filter((_, i) => i !== index));
  };

  const getLineTotal = (lineIds) => {
    return lines
      .filter(l => lineIds.includes(l.InvoiceLineID))
      .reduce((sum, l) => sum + (parseFloat(l.LineTotal) || parseFloat(l.Amount) || 0), 0);
  };

  const getAssignedLineIds = () => {
    return new Set(allocations.flatMap(a => a.lineIds));
  };

  const getUnassignedLines = () => {
    const assigned = getAssignedLineIds();
    return lines.filter(l => !assigned.has(l.InvoiceLineID));
  };

  const percentageTotal = allocations.reduce((sum, a) => sum + (parseFloat(a.percentage) || 0), 0);
  const amountTotal = allocations.reduce((sum, a) => sum + (parseFloat(a.amount) || 0), 0);

  const validate = () => {
    // Check all departments selected
    const emptyDept = allocations.find(a => !a.departmentId);
    if (emptyDept) return 'All allocations must have a department selected';

    // Check unique departments
    const depts = allocations.map(a => a.departmentId);
    if (new Set(depts).size !== depts.length) return 'Each department can only be used once';

    if (splitType === 'ByValue') {
      if (Math.abs(percentageTotal - 100) > 0.01) return `Percentages must sum to 100%. Current: ${percentageTotal.toFixed(2)}%`;
    } else {
      // ByPosition: all lines must be assigned
      const assigned = getAssignedLineIds();
      if (assigned.size !== lines.length) return `All ${lines.length} line items must be assigned. ${lines.length - assigned.size} unassigned.`;
      // Each allocation must have lines
      const emptyLines = allocations.find(a => a.lineIds.length === 0);
      if (emptyLines) return 'Each department must have at least one line item';
    }

    return null;
  };

  const handleSubmit = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      await onSplit({ splitType, allocations });
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to split invoice');
    } finally {
      setSubmitting(false);
    }
  };

  const formatAmount = (amt) => parseFloat(amt || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const usedDeptIds = allocations.map(a => a.departmentId).filter(Boolean);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center gap-3">
            <Scissors className="w-6 h-6 text-blue-600" />
            <div>
              <h2 className="text-lg font-bold text-gray-900">Split Invoice</h2>
              <p className="text-sm text-gray-600">
                {invoice.InvoiceNumber} &middot; {currencySymbol}{formatAmount(totalAmount)}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {/* Split Type Toggle */}
          <div>
            <label className="text-sm font-semibold text-gray-700 mb-2 block">Split Method</label>
            <div className="flex gap-2">
              <button
                onClick={() => setSplitType('ByValue')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 text-sm font-medium transition-all ${
                  splitType === 'ByValue'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                }`}
              >
                <Percent className="w-4 h-4" />
                Split by Value
              </button>
              <button
                onClick={() => setSplitType('ByPosition')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 text-sm font-medium transition-all ${
                  splitType === 'ByPosition'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                }`}
              >
                <ArrowLeftRight className="w-4 h-4" />
                Split by Position
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {splitType === 'ByValue'
                ? 'Allocate a percentage of the total invoice to each department'
                : 'Assign specific line items to each department'}
            </p>
          </div>

          {/* Allocations */}
          <div className="space-y-3">
            {allocations.map((alloc, index) => (
              <div key={index} className="border rounded-lg p-4 bg-gray-50">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-sm font-bold text-gray-500 w-6">#{index + 1}</span>
                  <select
                    value={alloc.departmentId}
                    onChange={(e) => handleDepartmentChange(index, e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Department...</option>
                    {departments.map(dept => {
                      const deptId = dept.DepartmentID;
                      const disabled = usedDeptIds.includes(deptId) && alloc.departmentId !== deptId;
                      return (
                        <option key={deptId} value={deptId} disabled={disabled}>
                          {dept.DepartmentName || dept.Name}
                        </option>
                      );
                    })}
                  </select>
                  {allocations.length > 2 && (
                    <button
                      onClick={() => removeAllocation(index)}
                      className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                      title="Remove"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {splitType === 'ByValue' ? (
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <label className="text-xs text-gray-500 mb-1 block">Percentage</label>
                      <div className="relative">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          value={alloc.percentage}
                          onChange={(e) => handlePercentageChange(index, e.target.value)}
                          className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <span className="absolute right-3 top-2.5 text-gray-400 text-sm">%</span>
                      </div>
                    </div>
                    <ArrowLeftRight className="w-4 h-4 text-gray-400 mt-5 shrink-0" />
                    <div className="flex-1">
                      <label className="text-xs text-gray-500 mb-1 block">Amount</label>
                      <div className="relative">
                        <span className="absolute left-3 top-2.5 text-gray-400 text-sm">{currencySymbol}</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={alloc.amount}
                          onChange={(e) => handleAmountChange(index, e.target.value)}
                          className="w-full px-3 py-2 pl-7 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">
                      Assigned Lines ({alloc.lineIds.length}) &middot; {currencySymbol}{formatAmount(getLineTotal(alloc.lineIds))}
                    </label>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {lines.map(line => {
                        const isAssigned = alloc.lineIds.includes(line.InvoiceLineID);
                        const assignedElsewhere = !isAssigned && getAssignedLineIds().has(line.InvoiceLineID);
                        return (
                          <label
                            key={line.InvoiceLineID}
                            className={`flex items-center gap-2 px-2 py-1.5 rounded text-sm cursor-pointer ${
                              isAssigned
                                ? 'bg-blue-50 border border-blue-200'
                                : assignedElsewhere
                                  ? 'opacity-40 cursor-not-allowed'
                                  : 'hover:bg-gray-100'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isAssigned}
                              disabled={assignedElsewhere}
                              onChange={() => handleLineToggle(index, line.InvoiceLineID)}
                              className="rounded text-blue-600"
                            />
                            <span className="flex-1 truncate">{line.LineNumber}. {line.Description || 'Unnamed'}</span>
                            <span className="text-gray-500 font-mono text-xs">
                              {currencySymbol}{formatAmount(line.LineTotal || line.Amount)}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Add Allocation Button */}
          <button
            onClick={addAllocation}
            className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-blue-400 hover:text-blue-600 transition-colors"
          >
            + Add Another Department
          </button>

          {/* Summary */}
          <div className={`p-3 rounded-lg border ${
            splitType === 'ByValue' && Math.abs(percentageTotal - 100) > 0.01
              ? 'bg-amber-50 border-amber-300'
              : 'bg-green-50 border-green-200'
          }`}>
            <div className="flex justify-between text-sm">
              <span className="font-medium text-gray-700">
                {splitType === 'ByValue' ? 'Total Allocation' : 'Lines Assigned'}
              </span>
              {splitType === 'ByValue' ? (
                <span className={`font-bold ${Math.abs(percentageTotal - 100) > 0.01 ? 'text-amber-700' : 'text-green-700'}`}>
                  {percentageTotal.toFixed(2)}% &middot; {currencySymbol}{formatAmount(amountTotal)}
                </span>
              ) : (
                <span className={`font-bold ${getAssignedLineIds().size === lines.length ? 'text-green-700' : 'text-amber-700'}`}>
                  {getAssignedLineIds().size} / {lines.length} lines &middot; {currencySymbol}{formatAmount(getLineTotal([...getAssignedLineIds()]))}
                </span>
              )}
            </div>
            {splitType === 'ByPosition' && getUnassignedLines().length > 0 && (
              <p className="text-xs text-amber-600 mt-1">
                {getUnassignedLines().length} line(s) still need to be assigned
              </p>
            )}
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
              <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t bg-gray-50">
          <p className="text-xs text-gray-500">
            Child invoices will be numbered {invoice.InvoiceNumber}/1, /2{allocations.length > 2 ? `, ... /${allocations.length}` : ''}
          </p>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg text-sm font-medium flex items-center gap-2"
            >
              {submitting ? (
                <><span className="animate-spin">⏳</span> Splitting...</>
              ) : (
                <><Scissors className="w-4 h-4" /> Split Invoice</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
